import React from 'react';
import { CheckCircle2, ChevronDown } from 'lucide-react';
import { ToggleSwitch } from '@/components/ToggleSwitch';

const FIGURE8 = 'M60,30 C75,6 104,6 104,30 C104,54 75,54 60,30 C45,6 16,6 16,30 C16,54 45,54 60,30 Z';

export const Figure8Animation = ({ color }) => (
  <svg viewBox="0 0 120 60" width="120" height="60" aria-hidden="true" data-testid="qibla-figure8">
    <path d={FIGURE8} fill="none" stroke={color} strokeOpacity="0.45" strokeWidth="2" strokeDasharray="4 4" />
    <g>
      <rect x="-6" y="-10" width="12" height="20" rx="2.5" fill={color} />
      <rect x="-4" y="-7.5" width="8" height="13" rx="1" fill="#0B1220" opacity="0.55" />
      <animateMotion dur="3.2s" repeatCount="indefinite" path={FIGURE8} rotate="auto" />
    </g>
  </svg>
);

export const CalibrationSteps = ({ steps, textColor, dotColor }) => (
  <ul className="list-none p-0 m-0 space-y-1.5" style={{ color: textColor, fontSize: 13, lineHeight: 1.6 }}>
    {steps.map((s, i) => (
      <li key={i} className="flex gap-2 items-start">
        <span className="shrink-0 font-bold" style={{ color: dotColor, minWidth: 14 }}>{i + 1}.</span>
        <span className="flex-1">{s}</span>
      </li>
    ))}
  </ul>
);

export const CalibrationTip = ({ tip, onDismiss, onDisable, textColors, labels }) => {
  if (tip === 'ready') {
    return (
      <div
        className="mx-4 mt-4 px-3 py-2 rounded-lg flex items-center gap-2"
        style={{ background: 'rgba(16,185,129,0.14)', border: '1px solid rgba(16,185,129,0.45)', color: textColors.primary }}
        data-testid="qibla-calibration-ready"
      >
        <CheckCircle2 size={16} color="#10B981" />
        <span className="text-sm font-semibold">{labels.ready}</span>
      </div>
    );
  }
  if (tip !== 'shown') return null;
  return (
    <div
      className="mx-4 mt-4 p-3 rounded-xl"
      style={{ background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.5)' }}
      data-testid="qibla-calibration-tip"
    >
      <div className="text-sm font-bold mb-1" style={{ color: textColors.primary }}>{labels.title}</div>
      <div className="text-[13px] mb-2" style={{ color: textColors.secondary }}>{labels.intro}</div>
      <div className="flex justify-center mb-2"><Figure8Animation color="#F59E0B" /></div>
      <CalibrationSteps steps={labels.steps} textColor={textColors.primary} dotColor="#F59E0B" />
      <div className="flex items-center justify-between gap-2 mt-3">
        <button
          type="button"
          onClick={onDisable}
          className="text-xs underline underline-offset-2"
          style={{ color: textColors.secondary, background: 'none', border: 'none' }}
          data-testid="qibla-calibration-never"
        >
          {labels.never}
        </button>
        <button
          type="button"
          onClick={onDismiss}
          className="px-4 py-1.5 rounded-lg text-sm font-bold"
          style={{ background: '#F59E0B', color: '#1A1204', border: 'none' }}
          data-testid="qibla-calibration-gotit"
        >
          {labels.gotIt}
        </button>
      </div>
    </div>
  );
};

export const CalibrationHintSection = ({ enabled, onToggle, showHowTo, onToggleHowTo, textColors, labels, isRTL }) => (
  <div className="mt-3 pt-3" style={{ borderTop: '1px solid rgba(148,163,184,0.25)' }}>
    <div className="flex items-center justify-between gap-3">
      <span className="text-[13px] font-semibold" style={{ color: textColors.primary }}>{labels.toggle}</span>
      <ToggleSwitch checked={enabled} onChange={onToggle} isRTL={isRTL} testId="qibla-calibration-toggle" label={labels.toggle} />
    </div>
    <button
      type="button"
      onClick={onToggleHowTo}
      className="mt-2 flex items-center gap-1 text-[13px] font-medium"
      style={{ color: textColors.accent, background: 'none', border: 'none', padding: 0 }}
      aria-expanded={showHowTo}
      data-testid="qibla-calibration-howto"
    >
      {labels.howTo}
      <ChevronDown size={14} style={{ transform: showHowTo ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }} />
    </button>
    {showHowTo && (
      <div className="mt-2" data-testid="qibla-calibration-howto-steps">
        <div className="flex justify-center mb-2"><Figure8Animation color={textColors.accent} /></div>
        <CalibrationSteps steps={labels.steps} textColor={textColors.secondary} dotColor={textColors.accent} />
      </div>
    )}
  </div>
);
